import type { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ExperienceName } from '~/features-shared/experience/interfaces/experience'
import { getDefaultExperienceIconAndColor } from '~/features-shared/experience/utils/default-experiences'
import type {
  FacetContentType,
  FacetDTO,
  FacetExperience,
  FacetResponse,
  FacetUnion,
} from '~/features-shared/facet/interfaces/facet'
import { FacetType } from '~/features-shared/facet/interfaces/facet'
import { isFacetContentType } from '~/features-shared/facet/utils/facets'

/**
 * Parses and returns the facet response as a base facet.
 *
 * @param facet - The facet from response.
 * @returns A base facet.
 */
function baseFacetParser(facet: FacetResponse): Omit<FacetUnion, 'type'> {
  return {
    communityUri: facet.community.subject6wuri,
    uri: facet.uri,
    position: facet.position,
    enabled: facet.enabled,
  }
}

/**
 * Parses and returns the facet response as a facet content type.
 *
 * @param facet - The facet from response.
 * @returns A facet content type.
 */
function facetContentTypeParser(facet: FacetResponse): FacetContentType {
  return {
    ...baseFacetParser(facet),
    type: FacetType.CONTENT_TYPE,
    model: (facet.type as IContentModel),
  }
}

/**
 * Parses and returns the facet response as a facet experience.
 *
 * @param facet - The facet from response.
 * @returns A facet experience.
 */
function facetExperienceParser(facet: FacetResponse): FacetExperience {
  const experience = facet.type as ExperienceName
  const finalIcon = getDefaultExperienceIconAndColor(experience)?.icon || facet.icon || ''
  const finalColor = getDefaultExperienceIconAndColor(experience)?.color || facet.color || ''

  return {
    ...baseFacetParser(facet),
    type: FacetType.EXPERIENCE,
    experience,
    label: facet.label,
    icon: finalIcon,
    color: finalColor,
    serviceName: facet.service_name || '',
    appId: facet.app_id || '',
    resourceId: facet.resource_id || undefined,
    resourceName: facet.resource_name || undefined,
    widgetPreference: facet.config,
  }
}

/**
 * Parses and returns the facet response as a facet.
 *
 * @param facet - The facet from response.
 * @returns A facet.
 */
export function facetParser(facet: FacetResponse): FacetUnion {
  const transformedFacet = { ...facet, type: facet.type.toLowerCase() }
  const newFacet: FacetUnion = transformedFacet.native
    ? facetContentTypeParser(transformedFacet)
    : facetExperienceParser(transformedFacet)

  return newFacet
}

/**
 * Parses and returns the facets response as facets.
 *
 * @param facets - The facets from response.
 * @returns The facets.
 */
export function facetsParser(facets: FacetResponse[]): FacetUnion[] {
  return facets.map(facetParser)
}

/**
 * Parses a facet for backend submission
 *
 * @param facet - The facet to parse.
 */
function facetParserToBackend(facet: FacetUnion): FacetDTO {
  const baseFacet: Pick<FacetDTO, 'uri' | 'enabled'> = {
    uri: facet.uri,
    enabled: facet.enabled,
  }

  if (isFacetContentType(facet)) {
    return {
      ...baseFacet,
      type: facet.model,
    }
  }
  else {
    return {
      ...baseFacet,
      type: facet.experience,

      label: facet.label,
      service_name: facet.serviceName,
      app_id: facet.appId,
      config: facet.widgetPreference,
      icon: facet.icon,
      color: facet.color,
      // Sending `null` to the backend indicates that no resource is currently attached to this experience facet, or it has been removed.
      resource_id: facet.resourceId ?? null,
      resource_name: facet.resourceName ?? null,
    }
  }
}

// TODO: TEST: Unit test this method
/**
 * Parses facets for backend submission
 *
 * @param facets - The facets to parse.
 */
export function facetsParserToBackend(facets: FacetUnion[]): FacetDTO[] {
  return facets.map(facetParserToBackend)
}
